import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { Grid, Icon, KeyValue, Typography } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useParams } from 'react-router-dom';
import { AccessArrangmentGetSummaryDetails } from '../../../api/candidate/access-arrangement/model';
import { candidateService } from '../../../api';


// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  header: {
    marginBottom: theme.spacing(6),
  },
  spaceBottom: {
    marginBottom: theme.spacing(2),
  },
  subheader: {
    marginBottom: theme.spacing(4),
  },
  keyValueContainer: {
    marginTop: theme.spacing(2),
  },
  documentsContainer: {
    marginTop: theme.spacing(1.5),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(2),
  },
  contentTemplateChildContainer: {
    marginBottom: theme.spacing(4),
  },

  personalDetailsContainer: {
    marginBottom: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
    marginButton: theme.spacing(3),
  },
  examLevel: {
    color: theme.color.text.textHeader,
  },
  downloadPdf: {
    display: 'flex',
    marginTop: theme.spacing(1.3),
    paddingLeft: theme.spacing(3),
  },
  icon: {
    color: theme.color.text.textTertiary,
    marginRight: theme.spacing(2),
  },
  filename: {
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('xl')]: {
      marginLeft: theme.spacing(-5.5),
    },
    [theme.breakpoints.down('xl')]: {
      marginLeft: theme.spacing(-4.5),
    },
    [theme.breakpoints.down('lg')]: {
      marginLeft: theme.spacing(-6),
    },
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(-3),
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
    },
  },
  filenameRow: {
    marginTop: theme.spacing(2),
  },
  divider: {
    marginBottom: theme.spacing(4),
  },
}));

interface IReviewApplication {
  data: AccessArrangmentGetSummaryDetails;
}

const ReviewApplication = ({ data }: IReviewApplication) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();

  const { data: individualReportFiles } =
    candidateService.accessArrangement.candidate.useGetIndividualReportSubmittedFiles(uuid);

  const medicalConditionString = data.accessArrangementRequestReviewModel.medicalConditionModel?.medicalConditionDropdownModel?.medicalConditions
    ?.map((medicalCondition) => medicalCondition.name)
    .join(', ');

  const medicalConditionFiles =
    data.accessArrangementRequestReviewModel.medicalConditionModel?.fileAttachedInfoModel ?? [];

  return (
    <>
      <Grid className={classes.contentTemplateChildContainer}>
        <Typography variant="h2" className={classes.subheader}>
          {t('accessArrangement->>ReviewApplication.Subheader2')}
        </Typography>
        <Grid className={classes.keyValueContainer}>
          <KeyValue
            id="candidateName"
            keyText={t('accessArrangement->>ReviewApplication.Condition')}
            valueText={medicalConditionString}
            capitalize={false}
          />
        </Grid>
        {data.accessArrangementRequestReviewModel.medicalConditionModel?.medicalConditionDropdownModel
          ?.remarks && (
            <Grid className={classes.keyValueContainer}>
              <KeyValue
                id="candidateName"
                keyText={t('accessArrangement->>ReviewApplication.Reason')}
                valueText={
                  data.accessArrangementRequestReviewModel.medicalConditionModel
                    ?.medicalConditionDropdownModel?.remarks
                }
              />
            </Grid>
          )}
        {medicalConditionFiles.length > 0 && (
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="medical-condition-documents"
              keyText={t('accessArrangement->>ReviewApplication.Uploaded')}
              valueText={
                <Grid className={classes.documentsContainer}>
                  {medicalConditionFiles.map((document) => (
                    <Grid
                      key={document.fileUuid}
                      container
                      direction="row"
                      className={classes.filenameRow}
                    >
                      <Grid xs={1}>
                        <Icon className={classes.icon} name={IconName.document} />
                      </Grid>
                      <Grid xs={11}>
                        <Typography className={classes.filename}>{document.fileName}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              }
            />
          </Grid>
        )}
      </Grid>
      {individualReportFiles && individualReportFiles.length > 0 && (
        <>
          <Divider className={classes.divider} />
          <Grid className={classes.contentTemplateChildContainer}>
          <Typography variant="h2" className={classes.subheader}>
            {t('accessArrangement->>ReviewApplication.IndividualReport')}
          </Typography>
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="individual-report-documents"
              keyText={t('accessArrangement->>ReviewApplication.Uploaded')}
              valueText={
                <Grid className={classes.documentsContainer}>
                  {individualReportFiles.map((document) => (
                    <Grid
                      key={document.fileUuid}
                      container
                      direction="row"
                      className={classes.filenameRow}
                    >
                      <Grid xs={1}>
                        <Icon className={classes.icon} name={IconName.document} />
                      </Grid>
                      <Grid xs={11}>
                        <Typography className={classes.filename}>{document.fileName}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              }
            />
          </Grid>
        </Grid>
        </>
      )}
    </>
  );
};

export default ReviewApplication;
